<template>
  <div class="service-list-container">
    <template v-if="activeTab === 'pending'">
      <div v-for="(section, idx) in sections" :key="idx">
        <div class="grid-column ai-c jc-s gap-16">
          <div class="sac__font-24 f-white bold">
            {{ section.title }}
            <span v-if="!section.loading">({{ section.list.length }})</span>
          </div>
          <button type="button" @click="toggleContainer(idx)">
            <span v-if="!toggleIdxs.includes(idx)" class="icon-luc-ChevronDown sac__font-24 f-white" />
            <span v-else class="icon-luc-ChevronRight sac__font-24 f-white" />
          </button>
        </div>

        <transition name="service-acc">
          <div v-if="!toggleIdxs.includes(idx)" class="sac-acc">
            <div v-if="section.loading" class="loading-container">
              <img src="@shared/assets/img/general/loading-mini-white.svg" alt="loading" />
            </div>
            <div v-else-if="section.list.length > 0">
              <div v-for="(item, itemIdx) in section.list" :key="itemIdx">
                <component :is="section.component" :serviceItem="item" />
              </div>
            </div>
            <div v-else class="no-data-container bg-n90 margin-top-16">
              No {{ section.title.toLowerCase() }} found
            </div>
          </div>
        </transition>
      </div>
      <div v-if="!sections.length && !loadingServiceRequest && !loadingLeads" class="no-list-container">
        No data found
      </div>
    </template>

    <template v-else>
      <div v-if="loadingServiceRequest || loadingLeads || loadingJobs" class="loading-container">
        <img src="@shared/assets/img/general/loading-mini-white.svg" alt="loading" />
      </div>
      <div v-else-if="allRequests.length === 0" class="no-list-container">
        No data found
      </div>
      <div
        v-for="(req, index) in allRequests"
        v-else
        :key="req.label"
      >
        <div v-if="req.label" class="grid-column ai-c jc-s gap-16">
          <div class="sac__font-24 f-white bold">
            {{ `${req.label} (${req.data.length})` }}
          </div>
          <button type="button" @click="toggleContainer(index)">
            <span v-if="!toggleIdxs.includes(index)" class="icon-luc-ChevronDown sac__font-24 f-white" />
            <span v-else class="icon-luc-ChevronRight sac__font-24 f-white" />
          </button>
        </div>
        <transition name="service-acc">
          <div v-if="!toggleIdxs.includes(index)" class="sac-acc">
            <div v-if="req.data.length > 0">
              <div
                v-for="(item, idx) in req.data"
                :key="idx"
              >
                <component :is="getComponent(item)" :serviceItem="item" />
              </div>
            </div>
          </div>
        </transition>
      </div>
    </template>
  </div>
</template>

<script>
import ServiceRequestCard from '@shared/components/service/ServiceRequestCard.vue';
import SmartLeadCard from '@shared/components/service/SmartLeadCard.vue';
import { mapGetters } from 'vuex';
import moment from 'moment';
import ActionStatus from '@shared/lib/constant/ActionStatus';
import serviceMixin from '../../mixins/serviceMixin';

export default {
  components: {
    ServiceRequestCard,
    SmartLeadCard,
  },
  mixins: [serviceMixin],
  data() {
    return {
      toggleIdxs: [],
    };
  },
  watch: {
    activeTab(oldVal, newVal) {
      if (oldVal !== newVal) {
        this.toggleIdxs = [];
      }
    },
  },
  computed: {
    ...mapGetters([
      'srList',
      'leadList',
      'actionJobList',
      'jobListFiltered',
    ]),
    allRequests() {
      let requests = [];

      if (this.activeTab === 'closed') {
        requests = [
          ...this.srList.map((item) => ({ ...item, type: 'service' })),
          ...this.leadList.map((item) => ({ ...item, type: 'opportunity' })),
          ...this.jobListFiltered.map((item) => ({ ...item, type: 'job' })),
        ];
      } else if (this.activeTab === 'incomplete'
        || this.activeTab === 'scheduled') {
        requests = this.jobListFiltered.map((item) => ({ ...item, type: 'job' }));
      } else if (this.activeTab === 'snoozed') {
        requests = this.leadList.map((item) => ({ ...item, type: 'opportunity' }));
      }

      if (this.activeTab === 'snoozed') {
        requests = requests.filter((req) => moment(req.snoozeUntil).isValid());
        requests = requests.sort((a, b) => {
          if (a.snoozeUntil < b.snoozeUntil) {
            return 1;
          }
          if (a.snoozeUntil > b.snoozeUntil) {
            return -1;
          }
          return 0;
        });

        if (!requests.length) {
          return [];
        }

        return [{
          label: null,
          data: requests,
        }];
      }
      requests = requests.sort((a, b) => {
        const aDate = this.getDate(a);
        const bDate = this.getDate(b);
        if (aDate > bDate) {
          return -1;
        }
        if (aDate < bDate) {
          return 1;
        }
        return 0;
      });

      const data = [{
        label: 'Today',
        data: [],
      },  {
        label: 'Tomorrow',
        data: [],
      }, {
        label: 'This Week',
        data: [],
      }, {
        label: 'Next Week',
        data: [],
      }, {
        label: 'Last Week',
        data: [],
      }, {
        label: 'This Month',
        data: [],
      }];
      requests.forEach((req) => {
        const date = this.getDate(req);
        const compare = moment(date);
        if (compare.isSame(moment(), 'day')) {
          data[0].data.push(req);
        }  else if (compare.isSame(moment().add(1, 'days'), 'day')) {
          data[1].data.push(req);
        } else if (compare.isSame(moment(), 'week')) {
          data[2].data.push(req);
        } else if (compare.isSame(moment().add(1, 'weeks'), 'week')) {
          data[3].data.push(req);
        } else if (compare.isSame(moment().subtract(1, 'weeks'), 'week')) {
          data[4].data.push(req);
        } else if (compare.isSame(moment(), 'month')) {
          data[5].data.push(req);
        } else {
          const label = compare.format('MMMM YYYY');
          const exist = data.findIndex((d) => d.label === label);
          if (exist > -1) {
            data[exist].data.push(req);
          } else {
            data.push({
              label,
              data: [req],
            });
          }
        }
      });

      return data.filter((d) => d.data.length > 0).sort((a, b) => moment(b.label, 'MMMM YYYY').toDate() - moment(a.label, 'MMMM YYYY').toDate());
    },
    sections() {
      const statusPendingTab = this.serviceFilters.find(
        (filter) => filter.key === 'statusPendingTab' && filter.active,
      );

      const activeOption = statusPendingTab?.activeOption?.value;

      const showSr = (activeOption && activeOption === 'Service Requests') || (!activeOption && this.srList.length);
      const showRsr = (activeOption && activeOption === 'Reschedule Requests') || (!activeOption && this.actionJobList.rescheduleList.length);
      const showCsr = (!activeOption && this.actionJobList.cancelList.length);
      const showLead = (activeOption && activeOption === 'Opportunities') || (!activeOption && this.leadList.length) || (this.loadingLeads && !activeOption);

      const sections = [];
      if (showCsr) {
        sections.push({
          title: 'Cancellation Requests',
          list: this.actionJobList.cancelList,
          loading: this.loadingServiceRequest,
          component: 'ServiceRequestCard',
        });
      }
      if (showRsr) {
        sections.push({
          title: 'Reschedule Requests',
          list: this.actionJobList.rescheduleList,
          loading: this.loadingServiceRequest,
          component: 'ServiceRequestCard',
        });
      }
      if (showSr) {
        sections.push({
          title: 'Service Requests',
          list: this.srList,
          loading: this.loadingServiceRequest,
          component: 'ServiceRequestCard',
        });
      }
      if (showLead) {
        sections.push({
          title: 'Opportunities',
          list: this.leadList,
          loading: this.loadingLeads,
          component: 'SmartLeadCard',
        });
      }

      return sections;
    },
  },
  methods: {
    toggleContainer(idx) {
      const index = this.toggleIdxs.indexOf(idx);
      if (index !== -1) {
        this.toggleIdxs.splice(index, 1);
      } else {
        this.toggleIdxs.push(idx);
      }
    },
    getDate(item) {
      if (this.activeTab === ActionStatus.Closed.toLowerCase()) {
        return item.actionStatusDate;
      }
      switch (item.type) {
        case 'service':
          return item.requestedDate || item.statusDate;
        case 'opportunity':
          return item.actionStatusDate;
        case 'job':
          return item.scheduleDate;
        default:
          return null;
      }
    },
    getComponent(item) {
      switch (item.type) {
        case 'service':
          return 'ServiceRequestCard';
        case 'opportunity':
          return 'SmartLeadCard';
        case 'job':
          return 'ServiceRequestCard';
        default:
          return null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.service-list-container {
  display: grid;
  gap: 42px;
  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .no-list-container {
    margin-top: 16px;
    padding: 40px;
    border-radius: 12px;
    text-align: center;
    font-size: 16px;
    font-weight: 500;
    color: $WHITE;
  }
  .service-acc-enter-active, .service-acc-leave-active {
    transition: max-height 0.2s ease-in-out, opacity 0.2s ease-in-out;
    overflow: hidden;
  }

  .service-acc-enter, .service-acc-leave-to {
    max-height: 0;
    opacity: 0;
  }

  .service-acc-enter-to, .service-acc-leave {
    max-height: 400px;
    opacity: 1;
  }
}
</style>
